/*
 * What a page says when `GET /songs/{id}/lyrics` (ENG-414) had nothing to
 * give, and in whose voice (ENG-415).
 *
 * Two surfaces show these lyrics and they are not addressing the same person.
 * The watch page's panel is talking to a listener who may not be signed in and
 * who is there to sing along; the performer's stand on the streamer page is
 * behind band membership, mid-show, with a song already started. "Sign in to
 * sing along with the lyrics" is the right sentence on the first and nonsense
 * on the second — a performer reading it is already signed in, is not singing
 * along to anybody, and what actually happened to them is an expired session.
 * So the endpoint's `error` code picks the sentence and `voice` picks which
 * branch of the catalogue it is read out of.
 *
 * This is a translation of the backend's error enum, which is neither page's
 * business to own: homing it beside one of them made the other import a
 * presentational component module purely to reach it.
 */

/** Which reader a sentence is for; the catalogue has a branch per voice. */
export const LyricsVoice = Object.freeze({
  /** The watch page's sing-along panel, addressing a listener. */
  LISTENER: 'listener',
  /** The streamer page's performance stand, addressing the performer. */
  PERFORMER: 'performer',
});

/*
 * Held as path segments rather than as a dotted prefix so that no string in
 * this file reads as a catalogue key: these are branches, not leaves, and the
 * localization gate's scan for referenced keys would flag one as a message
 * that does not exist.
 */
const CATALOGUE_BRANCH = Object.freeze({
  [LyricsVoice.LISTENER]: Object.freeze(['stream', 'lyrics']),
  [LyricsVoice.PERFORMER]: Object.freeze(['streamer', 'stage', 'lyrics']),
});

/**
 * A key under this voice's branch. The two branches carry the same leaf names
 * so that a sentence can be re-voiced without its caller changing, and
 * `lyricsMessages.test.js` holds every key either voice can build to one the
 * catalogue actually has — the gate's own scan cannot see a key that was
 * assembled rather than written out.
 */
export const lyricsKey = (suffix, voice = LyricsVoice.LISTENER) =>
  [...(CATALOGUE_BRANCH[voice] ?? CATALOGUE_BRANCH[LyricsVoice.LISTENER]), suffix].join('.');

/** What to say when there is nothing to show, by the envelope's `error` code. */
const UNAVAILABLE_SUFFIXES = Object.freeze({
  unauthorized: 'unavailable.signIn',
  // The auth middleware's 401s (ENG-591). fetchWrapper exempts this endpoint
  // from its auto-logout, so the page still looks signed in. A revoked
  // session is ended by signing in again; a denied login is an account that
  // may not sign in at all, which signing in again would only repeat. Neither
  // is "trying again shortly": a refused session is never retried.
  session_revoked: 'unavailable.signIn',
  login_denied: 'unavailable.accountDisabled',
  // Any 403 (ENG-595): a gateway turned the request away. Not "sign in",
  // since the session is fine, and not "back shortly", since it is not retried.
  forbidden: 'unavailable.refused',
  song_not_found: 'unavailable.notFound',
  song_lyrics_not_found: 'unavailable.notFound',
  song_is_instrumental: 'unavailable.instrumental',
  song_lyrics_source_unknown: 'unavailable.sourceUnknown',
});

/*
 * Everything else — the 503s (provider down, no key, daily budget spent), the
 * 429, a malformed id, an internal error — is "come back later" to a reader.
 * They differ in whose fault they are and in how long later, which matters to
 * an operator reading logs and not to somebody watching or playing a show.
 */
export const unavailableKey = (code, voice = LyricsVoice.LISTENER) =>
  lyricsKey(UNAVAILABLE_SUFFIXES[code] ?? 'unavailable.temporary', voice);

export default unavailableKey;
